import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    Package,
    MapPin,
    Truck,
    Clock,
    CheckCircle2,
    Circle,
    ArrowRight,
    ExternalLink,
    AlertTriangle,
} from "lucide-react";
import useSeo from "@/hooks/useSeo";

/**
 * Endpoint served by:
 *   - Production (Hostinger):  /api/track.php  (public/api/track.php)
 *   - Preview (Emergent):      /api/track.php  (FastAPI dev proxy)
 * Both routes accept `?waybill=<id>` and return the raw Logix JSON.
 */
const TRACK_ENDPOINT = "/api/track.php";
const PORTAL_URL = "https://beeta.logixerp.com/portal/Login?c=BEETA";

// Timeline stages we want to visualise (in order). Any actionLabel from the
// Logix API that doesn't fit these buckets is still shown chronologically
// under "Latest events" below the visual timeline.
const STAGE_ORDER = [
    { key: "pickup", label: "Picked up", match: ["pickup"] },
    { key: "origin", label: "At origin", match: ["at origin", "in scan"] },
    { key: "transit", label: "In transit", match: ["in transit"] },
    { key: "arrived", label: "Arrived at destination", match: ["stock in", "at warehouse"] },
    { key: "ofd", label: "Out for delivery", match: ["out for delivery"] },
    { key: "delivered", label: "Delivered", match: ["delivered", "partial delivery", "rto delivered"] },
];

function normaliseLabel(s = "") {
    return s.toLowerCase().replace(/\s+/g, " ").trim();
}

// The Logix top-level `currentStatus` is unreliable — it can read "Delivered"
// while the scan history only shows origin/transit events. A shipment is only
// treated as delivered when the status is exactly "Delivered" AND the scan
// history (or actualDeliveryDate) corroborates it.
function isDeliveredShipment(data, events) {
    if (normaliseLabel(data.currentStatus) !== "delivered") return false;
    if ((data.actualDeliveryDate || "").trim()) return true;
    // An attached ePOD or a delivery run sheet is proof of delivery even when
    // ops never recorded a "Delivered" scan event (common on FTL shipments).
    if ((data.epod || "").trim()) return true;
    if (Array.isArray(data.epods) && data.epods.length > 0) return true;
    if (Array.isArray(data.deliveryRunSheet) && data.deliveryRunSheet.length > 0) return true;
    return events.some(
        (ev) =>
            normaliseLabel(ev.actionLabel) === "delivered" ||
            normaliseLabel(ev.waybillStatus) === "delivered",
    );
}

function buildStages(events, isDelivered) {
    let highestIdx = -1;
    let latestIdx = -1;

    // Scan events can only ever promote up to "Out for delivery" — the final
    // "Delivered" stage is exclusively controlled by isDelivered below.
    const maxEventStage = STAGE_ORDER.length - 2;

    // events come newest-first from the API — walk chronological (oldest first)
    // and remember the furthest-along stage we can attribute.
    const chronological = [...events].reverse();
    for (const ev of chronological) {
        const label = normaliseLabel(ev.actionLabel);
        for (let i = 0; i <= maxEventStage; i++) {
            if (STAGE_ORDER[i].match.some((m) => label.includes(m))) {
                if (i > highestIdx) highestIdx = i;
                latestIdx = i;
                break;
            }
        }
    }

    // A confirmed delivery backfills the full timeline even when the API
    // omits intermediate scans.
    if (isDelivered) {
        highestIdx = STAGE_ORDER.length - 1;
        latestIdx = STAGE_ORDER.length - 1;
    }

    // Every stage up to and including highestIdx is considered "done" — this
    // backfills stages the API never emits an explicit event for.
    return STAGE_ORDER.map((s, i) => ({
        ...s,
        done: i <= highestIdx,
        current: i === latestIdx,
    }));
}

function formatEventDate(ev) {
    if (ev.dateTime) {
        try {
            const d = new Date(ev.dateTime);
            if (!Number.isNaN(d.getTime())) {
                return d.toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                });
            }
        } catch { /* fall through */ }
    }
    return `${ev.date || ""} ${ev.time || ""}`.trim();
}

export default function TrackShipment() {
    useSeo({
        title: "Track Shipment — Live Waybill Tracking",
        description:
            "Track your Beeta Logistics shipment in real time. Enter your waybill number to see the latest status, journey stages and destination.",
        path: "/track",
    });

    const [trackingId, setTrackingId] = useState("");
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [portalFallback, setPortalFallback] = useState("");
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const fetchWaybill = useCallback(async (id) => {
        setError("");
        setPortalFallback("");
        setResult(null);
        setLoading(true);
        try {
            const resp = await fetch(
                `${TRACK_ENDPOINT}?waybill=${encodeURIComponent(id)}`,
                { headers: { Accept: "application/json" } },
            );
            if (!resp.ok) {
                throw new Error(`Tracking service returned ${resp.status}.`);
            }
            const data = await resp.json();
            const item = data?.waybillTrackDetailList?.[0];
            if (!item) {
                setError(
                    "We couldn't find a shipment with that waybill number. Please double-check the number and try again.",
                );
                setPortalFallback(id);
                return;
            }
            setResult(item);
        } catch (e) {
            setError(
                "We're unable to reach the tracking service right now. You can try again in a moment or use our client portal below.",
            );
            setPortalFallback(id);
            console.error("Tracking fetch failed:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    // Auto-fetch when arriving with ?waybill=XXX in the URL
    // (e.g. from the header/sidebar quick-track inputs).
    useEffect(() => {
        const q = searchParams.get("waybill");
        if (q) {
            const id = q.trim().toUpperCase();
            setTrackingId(id);
            fetchWaybill(id);
        }
    }, [searchParams, fetchWaybill]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const id = trackingId.trim().toUpperCase();
        if (!id) {
            setError("Please enter a waybill number.");
            return;
        }
        setSearchParams({ waybill: id }, { replace: true });
        fetchWaybill(id);
    };

    const handleDemo = () => {
        setTrackingId("BLR69154");
        setSearchParams({ waybill: "BLR69154" }, { replace: true });
        fetchWaybill("BLR69154");
    };

    return (
        <div data-testid="track-page" className="bg-white">
            {/* HEADER */}
            <section className="relative pt-16 pb-20 bg-slate-50 border-b border-slate-200 overflow-hidden">
                <div className="absolute inset-0 bg-grid-slate opacity-40" />
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-5">
                        Waybill Tracker
                    </div>
                    <h1 className="font-display text-4xl sm:text-6xl font-black text-slate-900 tracking-tighter leading-[1.08] sm:leading-[0.95]">
                        Where is your shipment?
                    </h1>
                    <p className="mt-5 text-lg text-slate-600 max-w-xl mx-auto">
                        Enter your Beeta waybill number to see the live status, current
                        location and journey stages.
                    </p>
                </div>
            </section>

            {/* SEARCH */}
            <section className="py-16">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <form
                        onSubmit={handleSubmit}
                        data-testid="track-form"
                        className="relative bg-white border border-slate-200 rounded-2xl shadow-lg p-3 flex flex-col sm:flex-row gap-3"
                    >
                        <div className="flex-1 flex items-center gap-3 px-4">
                            <Search className="w-5 h-5 text-slate-400" />
                            <input
                                data-testid="track-input"
                                type="text"
                                value={trackingId}
                                onChange={(e) => setTrackingId(e.target.value)}
                                placeholder="Enter waybill number (e.g. BLR69154)"
                                className="flex-1 py-3 bg-transparent outline-none text-slate-900 placeholder-slate-400"
                                autoComplete="off"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="track-submit"
                            disabled={loading}
                            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-slate-900 text-white font-semibold hover:bg-blue-600 transition-colors disabled:opacity-60"
                        >
                            {loading ? "Tracking…" : "Track"}
                            {!loading && <ArrowRight className="w-4 h-4" />}
                        </button>
                    </form>

                    <div className="mt-4 flex items-center justify-between text-sm">
                        <div className="text-slate-500">
                            Don&apos;t have a number?{" "}
                            <button
                                type="button"
                                data-testid="track-demo-btn"
                                onClick={handleDemo}
                                className="text-blue-600 font-semibold hover:underline"
                            >
                                Try a sample
                            </button>
                        </div>
                        <a
                            href={PORTAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="track-portal-link"
                            className="text-slate-500 hover:text-blue-600 inline-flex items-center gap-1"
                        >
                            Open client portal
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    </div>

                    {error && (
                        <div
                            data-testid="track-error"
                            className="mt-5 rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm flex items-start gap-3"
                        >
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <div className="flex-1">
                                <div>{error}</div>
                                {portalFallback && (
                                    <a
                                        href={PORTAL_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="track-portal-fallback"
                                        className="mt-2 inline-flex items-center gap-1 font-semibold text-red-900 hover:underline"
                                    >
                                        Track on the client portal instead
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* RESULT */}
            <AnimatePresence>
                {result && (
                    <motion.section
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className="pb-24 sm:pb-32"
                        data-testid="track-result"
                    >
                        <ResultView data={result} />
                    </motion.section>
                )}
            </AnimatePresence>

            {!result && !error && (
                <section className="pb-24">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-5">
                        {[
                            { icon: MapPin, t: "Live status", d: "Real-time updates streamed from our operations tower — always current." },
                            { icon: Clock, t: "Every checkpoint", d: "See every scan from pickup to delivery, with time-stamped events." },
                            { icon: CheckCircle2, t: "Portal fallback", d: "Prefer the full portal? Log in with your client credentials any time." },
                        ].map((c) => {
                            const Icon = c.icon;
                            return (
                                <div key={c.t} className="p-7 rounded-2xl border border-slate-200 bg-slate-50">
                                    <Icon className="w-6 h-6 text-blue-600 mb-4" />
                                    <h3 className="font-display text-lg font-bold text-slate-900 mb-2">{c.t}</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}

function ResultView({ data }) {
    const events = Array.isArray(data.waybillTrackingDetail) ? data.waybillTrackingDetail : [];
    const isComplete = isDeliveredShipment(data, events);
    const stages = buildStages(events, isComplete);
    // When the top-level status can't be corroborated, show the latest scan's
    // status instead (e.g. "In Transit") — it reflects reality more closely.
    const currentStatus = isComplete
        ? "Delivered"
        : events.find((ev) => (ev.waybillStatus || "").trim())?.waybillStatus ||
          events[0]?.actionLabel ||
          data.currentStatus ||
          "In Progress";
    const origin = data.consignorCity || data.bookingLocation || data.bookingOu || "—";
    const destination = data.consigneeCity || data.deliveryLocation || data.deliveryOu || "—";

    const doneCount = stages.filter((s) => s.done).length;
    const latestEvent = events[0];

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Summary */}
            <div className="bg-slate-900 text-white rounded-3xl p-8 md:p-10 overflow-hidden relative">
                <div className="absolute inset-0 bg-dot-slate opacity-[0.06]" />
                <div className="relative grid md:grid-cols-2 gap-6 items-start">
                    <div>
                        <div
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wider mb-5 ${
                                isComplete
                                    ? "bg-green-500/15 border-green-500/30 text-green-300"
                                    : "bg-blue-500/15 border-blue-500/30 text-blue-300"
                            }`}
                        >
                            <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                    isComplete ? "bg-green-400" : "bg-blue-400 animate-pulse"
                                }`}
                            />
                            {currentStatus}
                        </div>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">
                            Waybill Number
                        </div>
                        <div
                            data-testid="track-result-waybill"
                            className="font-display text-3xl font-black tracking-tight"
                        >
                            {data.waybillNumber}
                        </div>

                        <div className="mt-8 flex items-center gap-3 text-slate-300">
                            <span className="font-semibold truncate">{origin}</span>
                            <div className="flex-1 h-px bg-slate-700 relative min-w-[40px]">
                                <div
                                    className={`absolute inset-y-0 left-0 ${isComplete ? "w-full bg-green-500" : "w-3/5 bg-blue-500"}`}
                                />
                                <Truck
                                    className={`absolute -top-2 ${isComplete ? "left-[calc(100%-1.25rem)] text-green-400" : "left-[60%] text-blue-400"} w-5 h-5`}
                                />
                            </div>
                            <span className="font-semibold truncate">{destination}</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <InfoTile
                            icon={Package}
                            label="Service"
                            value={data.service || data.paymentType || "—"}
                        />
                        <InfoTile
                            icon={Truck}
                            label="Current OU"
                            value={data.currentOu || "—"}
                        />
                        <InfoTile
                            icon={Clock}
                            label="Booked"
                            value={data.creationDate || data.bookingDate || "—"}
                        />
                        <InfoTile
                            icon={MapPin}
                            label="Checkpoints"
                            value={`${doneCount} / ${stages.length}`}
                        />
                    </div>
                </div>

                {latestEvent && (
                    <div className="relative mt-8 pt-6 border-t border-slate-800 text-sm text-slate-300">
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mr-3">
                            Latest event
                        </span>
                        <span className="font-semibold text-white">
                            {latestEvent.actionLabel}
                        </span>
                        <span className="ml-2 text-slate-400">
                            · {formatEventDate(latestEvent)}
                        </span>
                        {latestEvent.updatedOU && (
                            <span className="ml-2 text-slate-500">· {latestEvent.updatedOU}</span>
                        )}
                    </div>
                )}
            </div>

            {/* Timeline */}
            <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-8 md:p-10">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">
                    Journey
                </div>
                <h2 className="font-display text-3xl font-black text-slate-900 tracking-tighter mb-8">
                    Checkpoint timeline
                </h2>
                <ol className="relative">
                    <div className="absolute left-4 top-2 bottom-2 w-px bg-slate-200" />
                    {stages.map((stage) => (
                        <li key={stage.key} className="relative pl-14 pb-8 last:pb-0">
                            <div
                                className={`absolute left-0 top-0 w-9 h-9 rounded-full flex items-center justify-center ${
                                    stage.current
                                        ? "bg-blue-600 text-white ring-4 ring-blue-100"
                                        : stage.done
                                        ? "bg-green-500 text-white"
                                        : "bg-white border-2 border-slate-300 text-slate-400"
                                }`}
                            >
                                {stage.done ? (
                                    <CheckCircle2 className="w-5 h-5" />
                                ) : (
                                    <Circle className="w-5 h-5" />
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
                                <div className={`font-display text-lg font-bold ${stage.current ? "text-blue-600" : stage.done ? "text-slate-900" : "text-slate-400"}`}>
                                    {stage.label}
                                </div>
                                <div className="text-sm font-semibold text-slate-500">
                                    {stage.current
                                        ? "Latest stage"
                                        : stage.done
                                        ? "Completed"
                                        : "Upcoming"}
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>

                {events.length > 0 && (
                    <div className="mt-10 pt-8 border-t border-slate-200">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
                            Recent scan history
                        </div>
                        <ul className="space-y-3 text-sm">
                            {events.slice(0, 8).map((ev) => (
                                <li
                                    key={ev.id}
                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3"
                                >
                                    <div>
                                        <div className="font-semibold text-slate-900">
                                            {ev.actionLabel}
                                        </div>
                                        {ev.updatedOU && (
                                            <div className="text-xs text-slate-500">
                                                {ev.updatedOU}
                                                {ev.remarks && ev.remarks !== "OK" && ` · ${ev.remarks}`}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-xs font-medium text-slate-500">
                                        {formatEventDate(ev)}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t border-slate-200 text-sm">
                    <div className="text-slate-500">
                        Need the full operational view — invoices, ePODs, package list?
                    </div>
                    <a
                        href={PORTAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="track-portal-cta"
                        className="inline-flex items-center gap-2 font-semibold text-blue-600 hover:text-blue-700"
                    >
                        Open client portal
                        <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </div>
        </div>
    );
}

function InfoTile({ icon: Icon, label, value }) {
    return (
        <div className="bg-slate-800/50 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <div className="font-display font-bold text-white text-base truncate">
                {value}
            </div>
        </div>
    );
}
